import { useTranslation } from 'react-i18next'
import { Link, useRouterState } from '@tanstack/react-router'
import { useNavStore } from '@/stores/nav-store'

/**
 * 左侧模块栏 —— 把**两个互不相干的模块**摆在同一个层级上。
 *
 * ⚠⚠ 这两个模块不是「同一个助手的两个标签页」，界面必须把这件事说清楚：
 *   · **旅行规划**：走网关 → supervisor → 机票/酒店/打车子图，会发卡片、会停在
 *     「选哪个航班」这类断点上，会话 id 是客户端生成的 UUID，记忆按 `user_id` 隔离；
 *   · **通用助手**：一台**独立的服务**（`universal_mode`），有联网搜索/深度思考/配图，
 *     会话 id 是服务端自增的数字，历史存在另一套表里。
 *   两边的会话**互相看不见**，「新对话」也各管各的。用户要是以为是一个东西的两个模式，
 *   就会在通用助手里问「帮我订刚才那班机票」——而那边压根没有订票能力，
 *   也读不到旅行规划那边的上下文。所以每条下面那句副标题不是装饰，是**能力边界**。
 *
 * ⚠ 窄屏是抽屉（默认收起），`md` 起是常驻列。窄屏上常驻一条 224px 的栏，
 *   390px 的屏就只剩 166px 给正文了。
 */

/** 一个模块。⚠ 副标题写**能做什么**，不写形容词。 */
const MODULES = [
  {
    to: '/',
    exact: true,
    icon: '✈',
    titleKey: 'nav.travel',
    descKey: 'nav.travelDesc',
  },
  {
    to: '/plans',
    exact: true,
    icon: '▤',
    titleKey: 'nav.plans',
    descKey: 'nav.plansDesc',
  },
  {
    to: '/assistant',
    exact: false,
    icon: '✦',
    titleKey: 'nav.assistant',
    descKey: 'nav.assistantDesc',
  },
] as const

/**
 * 窄屏上打开模块栏的按钮。放在**各页标题栏最左**。
 *
 * ⚠ `md:hidden`：宽屏模块栏常驻，再给一颗开合键只会让人以为能把它收起来（收不起来）。
 */
export function SidebarToggle() {
  const { t } = useTranslation()
  const openSidebar = useNavStore((s) => s.openSidebar)
  return (
    <button
      type="button"
      onClick={openSidebar}
      aria-label={t('nav.openModules')}
      className="-ml-1 rounded-lg px-2 py-1 text-neutral-500 hover:bg-neutral-100 md:hidden"
    >
      {/* 三条横线。⚠ 用 SVG 而不是「≡」字符：后者在不同字体下高低不一 */}
      <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
        <path
          d="M4 7h16M4 12h16M4 17h16"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </button>
  )
}

export function AppSidebar() {
  const { t } = useTranslation()
  const open = useNavStore((s) => s.sidebarOpen)
  const closeSidebar = useNavStore((s) => s.closeSidebar)
  /*
    ⚠ 自己判高亮，不用 `Link` 的 `activeProps`：这一栏要把**整个模块**点亮 ——
      人在 `/assistant` 的任何子路由下，「通用助手」都得是选中态。
    ⚠ 旅行规划那条必须 `exact`，否则 `/` 会把所有路径都吃成选中。
  */
  const pathname = useRouterState({ select: (state) => state.location.pathname })

  return (
    <>
      {/*
        窄屏抽屉的背板。⚠ 必须有：没有它，抽屉开着时点正文既不关抽屉也点不到东西，
          用户会以为界面卡住了。
      */}
      {open ? (
        <button
          type="button"
          aria-label={t('common.cancel')}
          onClick={closeSidebar}
          className="fixed inset-0 z-30 bg-neutral-900/20 md:hidden"
        />
      ) : null}

      {/*
        ⚠ 过渡写 `transition-[translate]` 而不是 `transition-transform`：Tailwind v4 的
          `translate-x-*` 落在 **`translate` 属性**上，不是 `transform` —— 盯着 transform
          的话这条抽屉是**瞬移**的（属性对不上，过渡根本不触发），而且没有任何报错。
      */}
      <nav
        aria-label={t('nav.modules')}
        className={`fixed inset-y-0 left-0 z-40 flex w-56 shrink-0 flex-col gap-1 border-r border-neutral-100 bg-white px-2 pt-[calc(0.75rem+var(--spacing-safe-top))] pb-[var(--spacing-safe-bottom)] transition-[translate] duration-200 md:static md:z-auto md:translate-x-0 ${
          open ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <p className="px-2 pt-1 pb-3 text-base font-semibold">{t('appName')}</p>

        {MODULES.map((module) => {
          const active = module.exact ? pathname === module.to : pathname.startsWith(module.to)
          return (
            <Link
              key={module.to}
              to={module.to}
              onClick={closeSidebar}
              aria-current={active ? 'page' : undefined}
              className={`flex flex-col gap-0.5 rounded-xl px-3 py-2.5 transition-colors ${
                active ? 'bg-neutral-900 text-white' : 'text-neutral-700 hover:bg-neutral-100'
              }`}
            >
              <span className="flex items-center gap-2 text-sm font-medium">
                <span aria-hidden="true">{module.icon}</span>
                {t(module.titleKey)}
              </span>
              {/*
                ⚠ 副标题在选中态下也要读得出来：`text-neutral-400` 压在深色底上
                  对比度只有 2.6:1，达不到 4.5:1（WCAG 1.4.3）。
              */}
              <span className={`text-xs ${active ? 'text-neutral-300' : 'text-neutral-400'}`}>
                {t(module.descKey)}
              </span>
            </Link>
          )
        })}

        {/*
          ⚠⚠ 这一句不是脚注，是**红线**：两个模块的会话与记忆完全不互通。
            不写的话，用户在通用助手里接着问「刚才那班机票」是必然会发生的事 ——
            那边既没有订票能力，也读不到这边的上下文，而它**不会报错**，
            只会一本正经地编一段出来。
        */}
        <p className="mt-auto px-3 py-3 text-[11px] leading-relaxed text-neutral-400">
          {t('nav.isolationHint')}
        </p>
      </nav>
    </>
  )
}
